// Phase 18 Plan 06 — POLISH-11 Text-size segmented pill.
//
// Three values: '1.0' / '1.15' / '1.30'. Wired to the $fontScale persistent
// atom (apps/web/src/stores/fontScale.ts); RootApp mirrors the chosen value
// onto documentElement.dataset.fontScale so the CSS calc(N × var(--font-scale))
// chain in globals.css rescales every font-size token.
//
// Uses the shared Seg primitive (apps/web/src/ui/components/Seg.tsx) — Plan
// 18-02 added arrow-key navigation + roving tabindex to Seg, so this toggle
// inherits both for free. fieldset + legend wrap the picker for screen-reader
// semantics matching LanguageToggle / ReduceMotionToggle (the older sibling
// toggles predate Seg and use a hand-rolled SegmentButton instead — they will
// be migrated to Seg in a later plan).

import { useStore } from '@nanostores/react'
import type { JSX } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { $fontScale, type FontScale } from '../../stores/fontScale.js'
import { Seg } from '../components/Seg.js'

export function FontScaleToggle(): JSX.Element {
  const value = useStore($fontScale)
  const intl = useIntl()
  const groupLabel = intl.formatMessage({
    id: 'settings.font_scale.label',
    defaultMessage: 'Text size',
  })
  return (
    <fieldset
      data-testid="font-scale-toggle"
      aria-label={groupLabel}
      className="picker-row"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
        border: 'none',
        margin: 0,
        padding: 0,
      }}
    >
      <legend
        style={{
          fontFamily: 'Fredoka, sans-serif',
          fontWeight: 700,
          fontSize: '16px',
          color: 'var(--color-text-primary)',
          padding: 0,
          marginBottom: '4px',
        }}
      >
        <FormattedMessage
          id="settings.font_scale.label"
          defaultMessage="Text size"
        />
      </legend>
      <Seg<FontScale>
        ariaLabel={groupLabel}
        value={value}
        onChange={(next) => $fontScale.set(next)}
        options={[
          {
            value: '1.0',
            label: (
              <FormattedMessage
                id="settings.font_scale.opt.default"
                defaultMessage="Default"
              />
            ),
          },
          {
            value: '1.15',
            label: (
              <FormattedMessage
                id="settings.font_scale.opt.large"
                defaultMessage="Large"
              />
            ),
          },
          {
            value: '1.30',
            label: (
              <FormattedMessage
                id="settings.font_scale.opt.larger"
                defaultMessage="Larger"
              />
            ),
          },
        ]}
      />
    </fieldset>
  )
}
